import React, { useState, useEffect } from 'react';
import {
    Modal, Box, Typography, TextField, Button, Divider, Select, MenuItem,
    FormControl, InputLabel, Alert, IconButton, Grid
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment-timezone';
import axios from 'axios';
import { Lend } from '../../../../models/lend';
import { Constant } from '../../../../models/constant';

const getPeriodCount = (termInDays: number, typeConstantId?: number | null): number => {
    if (!Number.isFinite(termInDays) || termInDays <= 0) return 1;
    if (typeConstantId === 2) return Math.max(1, Math.round((termInDays / 30) * 4));
    if (typeConstantId === 3) return Math.max(1, Math.round(termInDays / 15));
    if (typeConstantId === 4) return Math.max(1, Math.round(termInDays / 30));
    return termInDays;
};

interface ConstantModalProps {
    open: boolean;
    onClose: () => void;
    officeId: string;
    // constantId: string;
    collectId: string;
    userId: string;
    client: Lend | null;
    showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
    showLoading: () => void;
    hideLoading: () => void;
    onPaymentSuccess: () => void;
}

const modalStyle = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: '90vw',
    maxWidth: 500,
    height: '120vh',
    maxHeight: 600,
    bgcolor: "background.paper",
    borderRadius: 2,
    boxShadow: 2,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    overflowY: 'auto',
};

const ConstantModal: React.FC<ConstantModalProps> = ({
    open, onClose, client, showAlert, collectId, userId, officeId, showLoading, hideLoading, onPaymentSuccess
}) => {
    const [constants, setConstants] = useState<Constant[]>([]);
    const [constantId, setConstantid] = useState('');
    const [days_term, setDays_term] = useState('');
    const [utility, setUtility] = useState('');
    const [lendId, setLendId] = useState('');
    const [isWeekly, setIsWeekly] = useState(false);
    const [selectedTypeConstantId, setSelectedTypeConstantId] = useState<number | null>(null);
    const [routeDay, setRouteDay] = useState('');
    const [value, setValue] = useState('');
    const [total_value, setTotal_value] = useState(0);
    const [daily_pay_value, setDaily_pay_value] = useState(0);
    const [location, setLocation] = useState<string | null>(null);

    useEffect(() => {
        const fetchConstants = async () => {
            try {
                const { data } = await axios.get(`/constantofficescomplete/${officeId}`);
                setConstants(data.constants);
            } catch {
                showAlert('Error al cargar las constantes.', 'error');
            }
        };

        if (officeId) fetchConstants();
    }, [officeId]);

    useEffect(() => {
        if (!constantId || selectedTypeConstantId != null || constants.length === 0) {
            return;
        }

        const selected = constants.find((constant) => String(constant.id) === String(constantId));
        if (!selected) {
            return;
        }

        setSelectedTypeConstantId(Number(selected.type_constant?.id) || null);
        setIsWeekly(Number(selected.type_constant?.id) === 2);
    }, [constantId, selectedTypeConstantId, constants]);

    useEffect(() => {
        const parsedValue = parseFloat(value) || 0;
        const parsedUtility = parseFloat(utility) || 0;
        const parsedTerm = parseInt(days_term) || 1;
        const periodCount = getPeriodCount(parsedTerm, selectedTypeConstantId);
        const total = parsedValue + (parsedValue * parsedUtility / 100);
        const daily = Math.round(total / periodCount);
        setTotal_value(total);
        setDaily_pay_value(daily);
    }, [value, days_term, utility, selectedTypeConstantId]);

    const paymentCount = getPeriodCount(parseInt(days_term) || 0, selectedTypeConstantId);

    useEffect(() => {
        if (open && client) {
            setLendId(client.id?.toString() || '');
            //console.log('client constant:', client.id);
            setConstantid(client.constant?.id?.toString() || '');
            setDays_term(client.constant?.term?.toString() || '');
            setUtility(client.constant?.utility?.toString() || '');
            setSelectedTypeConstantId(Number(client.constant?.type_constant?.id) || null);
            setIsWeekly(client.constant?.type_constant?.id === 2);
            setValue(client.value?.toString() || '');
            setTotal_value(Number(client.total_value) || 0);
            setDaily_pay_value(Number(client.daily_pay_value) || 0);
            const rd = (client as Lend & { route_day?: number | string | null }).route_day;
            setRouteDay(
                rd === 0 || rd === '0' || (rd != null && rd !== '')
                    ? String(rd)
                    : ''
            );
        }
    }, [open, client]);



    const getCoordinates = (): Promise<string> => {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                showAlert('Geolocation is not supported by this browser.', 'warning');
                return reject(new Error('Geolocation not supported'));
            }
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const lat = position.coords.latitude;
                    const lng = position.coords.longitude;
                    resolve(`${lat}, ${lng}`);
                },
                (error) => reject(error),
                { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
            );
        });
    };

    const handleUtilityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let value = event.target.value;
        if (value.length > 3) value = value.slice(0, 3);
        if (Number(value) > 100) value = value.slice(0, -1);
        if (Number(value) >= 0 && Number(value) <= 100) setUtility(value);
    };

    const handleSubmit = async () => {
        if (!constantId || !value) {
            showAlert('Debe completar todos los campos.', 'warning');
            return;
        }
        if (isWeekly && (routeDay === '' || routeDay === undefined)) {
            showAlert('Seleccione el día de cobro para la constante semanal.', 'warning');
            return;
        }

        try {
            showLoading();

            // Obtener coordenadas si están disponibles
            let coords: string | null = null;
            try {
                coords = await getCoordinates();
            } catch {
                showAlert('No se pudieron obtener las coordenadas.', 'info');
            }

            const formData = {
                lend_id: lendId,
                collect_id: collectId,
                constant_id: constantId,
                value,
                utility,
                user_id: userId,
                days_term: paymentCount,
                total_value,
                daily_pay_value,
                location: coords, // ✅ Se incluye aquí
                route_day: isWeekly ? routeDay : null,
            };
            //console.log('Form Data:', lendId);
            const url = `/lendsconstant/${lendId}`;
            await axios.put(url, formData);
            showAlert('Registro exitoso!', 'success');
            onPaymentSuccess();
            onClose();
        } catch {
            showAlert('Error registrando el préstamo.', 'error');
        } finally {
            hideLoading();
        }
    };


    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={modalStyle}>
                <Box display="flex" justifyContent="space-between" alignItems="center" p={2} bgcolor="primary.main" color="white">
                    <Typography variant="h6">Actualizar Constante</Typography>
                    <IconButton onClick={onClose} sx={{ color: "white" }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box p={3}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel id="constant-modal-constant-label">Seleccionar Constante</InputLabel>
                                <Select
                                    id="constant-modal-select"
                                    labelId="constant-modal-constant-label"
                                    value={constantId}
                                    label="Seleccionar Constante"
                                    displayEmpty
                                    renderValue={(selected) => {
                                        if (selected === '' || selected == null) {
                                            return '\u00a0';
                                        }
                                        const c = constants.find(
                                            (x) => String(x.id) === String(selected)
                                        );
                                        return c?.name ?? '';
                                    }}
                                    onChange={(e) => {
                                        const selectedId = Number(e.target.value);
                                        setConstantid(selectedId.toString());

                                        const selected = constants.find(c => c.id === selectedId);
                                        if (selected) {
                                            setDays_term(selected.term.toString());
                                            setUtility(selected.utility.toString());
                                            setSelectedTypeConstantId(Number(selected.type_constant.id));
                                            setIsWeekly(selected.type_constant.id === 2);
                                        }
                                    }}
                                    size="small"
                                >
                                    <MenuItem value="" disabled>
                                        <em>Seleccione constante</em>
                                    </MenuItem>
                                    {constants.map((constant) => (
                                        <MenuItem key={constant.id} value={constant.id.toString()}>
                                            {constant.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {isWeekly && (
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel id="constant-modal-route-day-label">Día de Cobro</InputLabel>
                                    <Select
                                        id="constant-modal-route-day"
                                        labelId="constant-modal-route-day-label"
                                        label="Día de Cobro"
                                        value={routeDay}
                                        onChange={(e) => setRouteDay(e.target.value)}
                                        size="small"
                                    >
                                        <MenuItem value="1">Lunes</MenuItem>
                                        <MenuItem value="2">Martes</MenuItem>
                                        <MenuItem value="3">Miércoles</MenuItem>
                                        <MenuItem value="4">Jueves</MenuItem>
                                        <MenuItem value="5">Viernes</MenuItem>
                                        <MenuItem value="6">Sábado</MenuItem>
                                        <MenuItem value="0">Domingo</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <TextField
                                label="Número de Cuotas"
                                value={paymentCount}
                                InputProps={{ readOnly: true }}
                                size="small"
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label="Valor Solicitado"
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                type="number"
                                size="small"
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label="% Utilidad"
                                value={utility}
                                onChange={handleUtilityChange}
                                type="number"
                                size="small"
                                InputProps={{ readOnly: true }}
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label="Valor Total a Pagar"
                                value={total_value.toFixed(0)}
                                InputProps={{ readOnly: true }}
                                size="small"
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label="Valor Periodo"
                                value={daily_pay_value.toFixed(0)}
                                InputProps={{ readOnly: true }}
                                size="small"
                                fullWidth
                            />
                        </Grid>
                    </Grid>

                </Box>
                <Box display="flex" justifyContent="space-between" p={2} borderTop={1} borderColor="divider">
                    <Button variant="outlined" onClick={onClose}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSubmit}>Guardar</Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default ConstantModal;
